import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@mui/material";
import { gtag } from 'ga-gtag';
import axios from 'axios';
import { FaArrowRight } from 'react-icons/fa';
import paths from "../../../paths";
import SnackbarAlert from "../../common/SnackbarAlert";
import { useSearchParams } from "react-router-dom";
import getGeoInfo from "../../common/common/getGeoInfo";
import "./SalesPageNew.css";
const S3_BASE = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/images';
const HeroImage = `${S3_BASE}/hero-features-william.webp`;
const AgentDesignerImg = `${S3_BASE}/agent-designer.jpg`;
const AgentWriterImg = `${S3_BASE}/agent-writer.jpg`;
const AgentGreeterImg = `${S3_BASE}/agent-greeter.jpg`;

const PRICING = {
   GBP: { symbol: "£", monthly: "29" },
   USD: { symbol: "$", monthly: "29" },
   EUR: { symbol: "€", monthly: "29" },
   SEK: { symbol: " kr", monthly: "399", suffix: true },
   AUD: { symbol: "AU$", monthly: "49" },
   CAD: { symbol: "CA$", monthly: "49" },
   NZD: { symbol: "NZ$", monthly: "60" },
};

const AGENT_STATES = [
   { avatar: AgentDesignerImg, name: "Design Agent", job: "Drafting Landing Page", status: "On standby", dotColor: "#10b981" },
   { avatar: AgentWriterImg, name: "Copywriting Agent", job: "5-email launch", status: "Drafting", dotColor: "#3b82f6" },
   { avatar: AgentGreeterImg, name: "Sales Agent", job: "Identifying Visitors", status: "Watching", dotColor: "#10b981" },
];

function HeroDashedLines() {
   return (
      <svg
         aria-hidden="true"
         style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
         viewBox="0 0 960 760"
         preserveAspectRatio="xMidYMid meet"
         fill="none"
      >
         <path d="M 430 -20 L 120 -20 Q 100 -20 100 0 L 100 680 Q 100 700 120 700 L 430 700" className="sp-hero-dash" />
         <path d="M 530 -20 L 840 -20 Q 860 -20 860 0 L 860 680 Q 860 700 840 700 L 530 700" className="sp-hero-dash" />
      </svg>
   );
}

function AgentNotification() {
   return (
      <div
         style={{
            position: 'absolute',
            left: '68%',
            transform: 'translateX(-50%)',
            top: '10%',
            width: '50%',
            maxWidth: 300,
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            animation: 'sp-agent-float 6s ease-in-out infinite',
         }}
      >
         {AGENT_STATES.map((s, idx) => (
            <div
               key={s.name}
               style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.5)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)',
                  border: '1px solid #f3f4f6',
                  padding: '10px 12px',
                  opacity: 0,
                  animation: 'sp-agent-loop 8s ease-in-out infinite',
                  animationDelay: `${idx * 300}ms`,
               }}
            >
               <img src={s.avatar} alt={s.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
               <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'none' }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'none' }}>{s.job}</div>
               </div>
               <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, borderRadius: 9999, background: '#f9fafb',
                  border: '1px solid #f3f4f6', padding: '4px 8px',
               }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dotColor }} />
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>{s.status}</span>
               </div>
            </div>
         ))}
      </div>
   );
}

function HeroTypingInput() {
   const items = ["courses", "digital products", "1:1 coaching", "communities", "group coaching"];
   const prefix = "I want to sell ";
   const containerRef = useRef(null);
   const [started, setStarted] = useState(false);
   const [prefixText, setPrefixText] = useState("");
   const [prefixDone, setPrefixDone] = useState(false);
   const [itemIndex, setItemIndex] = useState(0);
   const [text, setText] = useState("");
   const [deleting, setDeleting] = useState(false);

   useEffect(() => {
      if (!containerRef.current) return;
      const obs = new IntersectionObserver(
         (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setStarted(true); obs.disconnect(); } }); },
         { threshold: 0.3 }
      );
      obs.observe(containerRef.current);
      return () => obs.disconnect();
   }, []);

   useEffect(() => {
      if (!started || prefixDone) return;
      if (prefixText === prefix) { setPrefixDone(true); return; }
      const t = setTimeout(() => setPrefixText(prefix.slice(0, prefixText.length + 1)), 55);
      return () => clearTimeout(t);
   }, [started, prefixText, prefixDone]);

   useEffect(() => {
      if (!prefixDone) return;
      const current = items[itemIndex];
      if (!deleting && text === current) {
         const t = setTimeout(() => setDeleting(true), 1400);
         return () => clearTimeout(t);
      }
      if (deleting && text === "") {
         setDeleting(false);
         setItemIndex((i) => (i + 1) % items.length);
         return;
      }
      const t = setTimeout(() => {
         setText((prev) => deleting ? current.slice(0, prev.length - 1) : current.slice(0, prev.length + 1));
      }, deleting ? 35 : 70);
      return () => clearTimeout(t);
   }, [text, deleting, itemIndex, prefixDone]);

   return (
      <div ref={containerRef} style={{ minWidth: 0, flex: 1, padding: '0 12px', textAlign: 'left', fontSize: 14, color: 'var(--sp-ink)' }}>
         <span style={{ color: 'var(--sp-text-muted)' }}>{prefixText}</span>
         <span>{text}</span>
         <span style={{ marginLeft: 2, display: 'inline-block', width: 1, height: '1em', backgroundColor: 'var(--sp-ink)', verticalAlign: 'middle', animation: 'heroTypingCursorBlink 1s ease-in-out infinite' }} />
      </div>
   );
}

export default function Hero(props) {
   const { t } = useTranslation();
   const [searchParams] = useSearchParams();
   const [currency] = useState(() => getGeoInfo(true, true));

   const price = PRICING[currency] || PRICING.USD;
   const formattedPrice = price.suffix ? `${price.monthly}${price.symbol}` : `${price.symbol}${price.monthly}`;

   const getRegisterUrl = () => {
      let direct_url = '';
      const referralId = searchParams.get("id") || props.referralId;
      const passiveReferralId = searchParams.get("passive_id") || props.passiveReferralId;
      if (referralId != null && referralId !== '') {
         direct_url = '/become-a-creator/register?id=' + referralId;
      } else if (passiveReferralId != null && passiveReferralId !== '') {
         direct_url = '/become-a-creator/register?passive_id=' + passiveReferralId;
      } else {
         direct_url = '/become-a-creator/register';
      }
      // Append UTM params
      const utmParams = { utm_source: props.utmSource, utm_medium: props.utmMedium, utm_campaign: props.utmCampaign, utm_content: props.utmContent, utm_term: props.utmTerm };
      Object.entries(utmParams).forEach(([key, value]) => {
         if (value != null && value !== '') {
            direct_url += (direct_url.includes('?') ? '&' : '?') + key + '=' + value;
         }
      });
      return direct_url;
   };

   const handleCTA = () => {
      if (props.logged_in === true) {
         if (props.pre_influencer === true && props.influencer_bool !== true) {
            window.location.assign('/become-a-creator/start-free-trial');
         } else if (props.pre_influencer === false && props.influencer_bool !== true) {
            window.location.assign('/become-a-creator/register');
         } else {
            window.location.assign('/' + props.current_user);
         }
      } else {
         window.location.assign(getRegisterUrl());
      }
   };

   const getButtonText = () => {
      if (props.logged_in === true) {
         if (props.pre_influencer === true && props.influencer_bool === true) return t('marketing.hero.myHub');
         if (props.pre_influencer === true || props.influencer_bool === false) return t('marketing.hero.continueForFree');
         return t('marketing.hero.myHub');
      }
      return t('marketing.hero.start14DaysFree');
   };

   return (
      <section style={{ position: 'relative', overflow: 'hidden', paddingBottom: 48, paddingTop: 100 }}>
         {/* Text content */}
         <div style={{ position: 'relative', maxWidth: 960, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
            {/* "New 2026" pill */}
            <button
               type="button"
               onClick={() => {
                  const el = document.getElementById('agents-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
               }}
               style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 9999,
                  border: '1px solid rgba(59,85,243,0.25)', background: 'rgba(255,255,255,0.7)',
                  padding: '6px 14px', fontSize: 12, fontWeight: 500, color: 'var(--sp-ink)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', backdropFilter: 'blur(4px)',
                  cursor: 'pointer', marginBottom: 24, transition: 'border-color 0.2s, background 0.2s',
               }}
            >
               <span style={{
                  display: 'inline-flex', alignItems: 'center', borderRadius: 9999,
                  background: 'rgba(59,85,243,0.1)', padding: '2px 8px', fontSize: 10,
                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--sp-brand)',
               }}>
                  {t('salesPageNew.hero.badgeLabel')}
               </span>
               <span>{t('salesPageNew.hero.badgeText')}</span>
               <FaArrowRight style={{ width: 12, height: 12 }} />
            </button>

            {/* Main headline */}
            <h1 style={{
               fontSize: props.windowWidth < 380 ? 36 : (props.windowWidth < 768 ? 48 : 72),
               lineHeight: 1.05, letterSpacing: '-0.02em', color: 'var(--sp-ink)',
               fontWeight: 600, textTransform: 'uppercase', margin: 0, overflowWrap: 'break-word',
            }}>
               {t('salesPageNew.hero.heading')}
               <br />
               <span className="sp-accent-italic" style={{
                  textTransform: 'none',
                  fontSize: props.windowWidth < 380 ? 24 : (props.windowWidth < 768 ? 30 : (props.windowWidth < 1024 ? 40 : 48)),
               }}>
                  {t('salesPageNew.hero.subheading')}
               </span>
            </h1>

            {/* Subtitle */}
            <p style={{ margin: '24px auto 0', maxWidth: 512, fontSize: 18, color: 'var(--sp-text-soft)' }}>
               {t('salesPageNew.hero.priceSubtitle', { price: formattedPrice })}
            </p>

            {/* Search bar CTA */}
            <div style={{
               marginTop: 40, maxWidth: 672, marginLeft: 'auto', marginRight: 'auto',
               display: 'flex', flexDirection: props.windowWidth < 640 ? 'column' : 'row',
               alignItems: 'center', gap: props.windowWidth < 640 ? 12 : 8,
               borderRadius: 9999,
               border: props.windowWidth < 640 ? 'none' : '1px solid rgba(59,85,243,0.3)',
               background: props.windowWidth < 640 ? 'transparent' : '#fff',
               padding: props.windowWidth < 640 ? 0 : 8,
               boxShadow: props.windowWidth < 640 ? 'none' : '0 20px 60px -20px rgba(59,85,243,0.35)',
            }}>
               <div style={{
                  display: 'flex', flex: 1, alignItems: 'center', gap: 8, width: '100%',
                  borderRadius: 9999,
                  border: props.windowWidth < 640 ? '1px solid rgba(59,85,243,0.3)' : 'none',
                  background: props.windowWidth < 640 ? '#fff' : 'transparent',
                  padding: props.windowWidth < 640 ? 8 : 0,
                  boxShadow: props.windowWidth < 640 ? '0 20px 60px -20px rgba(59,85,243,0.35)' : 'none',
               }}>
                  <HeroTypingInput />
               </div>
               <button
                  className="sp-btn-primary"
                  onClick={handleCTA}
                  style={{
                     padding: '12px 20px', fontSize: 14, flexShrink: 0, whiteSpace: 'nowrap',
                     width: props.windowWidth < 640 ? '100%' : 'auto',
                  }}
               >
                  {t('salesPageNew.hero.startForFree')} <FaArrowRight style={{ width: 14, height: 14 }} />
               </button>
            </div>

            {/* Trust line */}
            <p style={{ marginTop: 20, fontSize: 14, color: 'var(--sp-text-muted)' }}>
               {t('salesPageNew.hero.trustedBy')} <span style={{ fontWeight: 600, color: 'var(--sp-ink)' }}>{t('salesPageNew.hero.trustedCount')}</span> {t('salesPageNew.hero.creatorEntrepreneurs')}
            </p>
         </div>

         {/* Phone mockup with dashed lines */}
         <div style={{ margin: '48px auto 0', display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: '80%', maxWidth: 825 }}>
               <HeroDashedLines />
               <img
                  src={HeroImage}
                  alt="Your withme.so hub on a phone"
                  style={{ position: 'relative', zIndex: 10, width: '100%', height: 'auto' }}
               />
               {props.windowWidth >= 768 && <AgentNotification />}
            </div>
         </div>

         {/* Snackbar for email validation */}
         <SnackbarAlert
            msg={props.emailSubmit?.message}
            backgroundColor={'#3B55F3'}
            open={props.emailSubmit?.open}
            severity={props.emailSubmit?.severity}
            handleClose={() => {
               if (props.setEmailSubmit) {
                  props.setEmailSubmit({
                     open: false,
                     severity: props.submitError ? 'error' : 'success',
                     message: ''
                  });
               }
            }}
         />
      </section>
   );
}
